<script setup lang="ts">
import { computed } from 'vue'

// Shared full-viewport gradient backdrop — three blurred, drifting color
// blobs — mounted once in App.vue behind every route. Animates continuously
// on a 3.2s/4s/3.6s cycle; pass `reactive` + `level` (e.g. live mic input
// from useVoiceRecorder, via the useAuroraReactivity singleton) to scale its
// intensity with audio, as RecordView does while actively recording. `level`
// is ignored unless `reactive` is true, so a stray nonzero value on other
// pages can't cause a pulse.
const props = withDefaults(defineProps<{ reactive?: boolean; level?: number }>(), { reactive: false, level: 0 })
const effectiveLevel = computed(() => (props.reactive ? props.level : 0))
</script>

<template>
  <div
    class="aurora-bg pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    :style="{ '--level': effectiveLevel }"
    aria-hidden="true"
  >
    <span class="aurora-bg__blob aurora-bg__blob--1 intensity-driven-gradient" />
    <span class="aurora-bg__blob aurora-bg__blob--2 intensity-driven-gradient" />
    <span class="aurora-bg__blob aurora-bg__blob--3 intensity-driven-gradient" />
  </div>
</template>

<style scoped>
/* Blurred radial-gradient "blob" layers, --level (0-1) reactive. Shares its
   blur/opacity mechanics with the .intensity-driven-gradient base class in
   style.css. */
.aurora-bg__blob {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  --intensity-opacity: calc(0.2 + var(--level, 0) * 0.4);
  --intensity-blur: 24px;
  --intensity-transition: 0.15s ease-out;
}

.aurora-bg__blob--1 {
  background: radial-gradient(circle, color-mix(in srgb, var(--color-primary) 70%, transparent) 0%, transparent 70%);
  animation: aurora-drift-1 3.2s ease-in-out infinite;
}

.aurora-bg__blob--2 {
  background: radial-gradient(circle, color-mix(in srgb, var(--color-secondary) 70%, transparent) 0%, transparent 70%);
  animation: aurora-drift-2 4s ease-in-out infinite;
  animation-delay: -1s;
}

.aurora-bg__blob--3 {
  background: radial-gradient(circle, color-mix(in srgb, var(--color-tertiary) 60%, transparent) 0%, transparent 70%);
  animation: aurora-drift-3 3.6s ease-in-out infinite;
  animation-delay: -0.6s;
}

@keyframes aurora-drift-1 {
  0%,
  100% {
    transform: translate(-8%, 4%) scale(calc(0.85 + var(--level, 0) * 0.6));
  }

  50% {
    transform: translate(8%, -6%) scale(calc(1 + var(--level, 0) * 0.75));
  }
}

@keyframes aurora-drift-2 {
  0%,
  100% {
    transform: translate(6%, -4%) scale(calc(0.9 + var(--level, 0) * 0.55));
  }

  50% {
    transform: translate(-10%, 8%) scale(calc(1.05 + var(--level, 0) * 0.7));
  }
}

@keyframes aurora-drift-3 {
  0%,
  100% {
    transform: translate(0%, 8%) scale(calc(0.8 + var(--level, 0) * 0.5));
  }

  50% {
    transform: translate(4%, -10%) scale(calc(1 + var(--level, 0) * 0.65));
  }
}

@media (prefers-reduced-motion: reduce) {
  .aurora-bg__blob {
    animation: none !important;
  }
}
</style>
